/**
 * TABLE COMPONENT - Apple Liquid Glass Design System
 * macOS Tahoe 26.2 & iOS 26.2
 * Based on WWDC25: Meet Liquid Glass
 *
 * Features:
 * - Ultra-subtle separators (8% opacity max)
 * - Morphing hover states with glass lift
 * - Liquid Glass pagination controls
 * - Smooth row animations
 * - Sortable columns with animated indicators
 */

import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  ChevronLeft,
  ChevronRight,
  MoreHorizontal
} from 'lucide-react'
import { cn } from '../../lib/utils'

export interface Column<T> {
  key: string
  label: string
  sortable?: boolean
  align?: 'left' | 'center' | 'right'
  width?: string
  render?: (item: T) => React.ReactNode
}

interface TableProps<T> {
  data: T[]
  columns: Column<T>[]
  onRowClick?: (item: T) => void
  actions?: (item: T) => React.ReactNode
  emptyMessage?: string
  emptyIcon?: React.ReactNode
  pageSize?: number
  showPagination?: boolean
  isLoading?: boolean
  stickyHeader?: boolean
}

export function Table<T extends Record<string, any>>({
  data,
  columns,
  onRowClick,
  actions,
  emptyMessage = 'No data available',
  emptyIcon,
  pageSize = 10,
  showPagination = true,
  isLoading = false,
  stickyHeader = false
}: TableProps<T>) {
  const [sortKey, setSortKey] = useState<string | null>(null)
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc')
  const [currentPage, setCurrentPage] = useState(1)

  // Sorting logic
  const sortedData = [...data].sort((a, b) => {
    if (!sortKey) return 0

    const aValue = a[sortKey]
    const bValue = b[sortKey]

    if (aValue === bValue) return 0

    const comparison = aValue > bValue ? 1 : -1
    return sortOrder === 'asc' ? comparison : -comparison
  })

  // Pagination logic
  const totalPages = Math.ceil(sortedData.length / pageSize)
  const startIndex = (currentPage - 1) * pageSize
  const endIndex = startIndex + pageSize
  const paginatedData = sortedData.slice(startIndex, endIndex)

  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortKey(key)
      setSortOrder('asc')
    }
  }

  const SortIcon = ({ columnKey }: { columnKey: string }) => {
    const baseClasses = "w-4 h-4 transition-all duration-fast"

    if (sortKey !== columnKey) {
      return (
        <ChevronsUpDown
          className={cn(baseClasses, "text-light-text-quaternary dark:text-dark-text-quaternary")}
        />
      )
    }

    return (
      <motion.div
        initial={{ scale: 0.8 }}
        animate={{ scale: 1 }}
        transition={{ type: "spring", damping: 15, stiffness: 300 }}
      >
        {sortOrder === 'asc' ? (
          <ChevronUp className={cn(baseClasses, "text-blue dark:text-blue-dark")} strokeWidth={2.5} />
        ) : (
          <ChevronDown className={cn(baseClasses, "text-blue dark:text-blue-dark")} strokeWidth={2.5} />
        )}
      </motion.div>
    )
  }

  // Loading skeleton
  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="overflow-hidden rounded-2xl liquid-material-regular">
          {/* Header skeleton */}
          <div className="border-b border-black/[0.08] dark:border-white/[0.08] px-4 py-3">
            <div className="flex gap-4">
              {columns.map((_, i) => (
                <div key={i} className="h-4 bg-light-bg-tertiary dark:bg-dark-bg-tertiary rounded-lg animate-pulse flex-1" />
              ))}
            </div>
          </div>
          {/* Row skeletons */}
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="border-b border-black/[0.06] dark:border-white/[0.06] last:border-0 px-4 py-4">
              <div className="flex gap-4">
                {columns.map((_, j) => (
                  <div key={j} className="h-5 bg-light-bg-tertiary dark:bg-dark-bg-tertiary rounded-lg animate-pulse flex-1" />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  // Empty state
  if (data.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center liquid-material-regular rounded-2xl">
        {emptyIcon && (
          <div className="mb-4 p-4 rounded-2xl liquid-material-clear">
            {emptyIcon}
          </div>
        )}
        <p className="text-body text-light-text-secondary dark:text-dark-text-secondary tracking-tight">
          {emptyMessage}
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Table */}
      <div className="overflow-x-auto rounded-2xl liquid-material-regular shadow-glass dark:shadow-dark-glass">
        <table className="w-full">
          <thead className={cn(stickyHeader && "sticky top-0 z-10")}>
            <tr className="border-b border-black/[0.08] dark:border-white/[0.08]">
              {columns.map((column) => (
                <th
                  key={column.key}
                  style={{ width: column.width }}
                  className={cn(
                    'px-4 py-3.5',
                    column.align === 'center' && 'text-center',
                    column.align === 'right' && 'text-right',
                    column.align !== 'center' && column.align !== 'right' && 'text-left',
                    column.sortable && 'cursor-pointer select-none',
                    'liquid-motion transition-colors duration-fast',
                    column.sortable && 'hover:bg-black/[0.02] dark:hover:bg-white/[0.02]'
                  )}
                  onClick={() => column.sortable && handleSort(column.key)}
                >
                  <div className={cn(
                    'flex items-center gap-2',
                    column.align === 'center' && 'justify-center',
                    column.align === 'right' && 'justify-end'
                  )}>
                    <span className="text-subheadline font-semibold text-light-text-secondary dark:text-dark-text-secondary tracking-tight">
                      {column.label}
                    </span>
                    {column.sortable && <SortIcon columnKey={column.key} />}
                  </div>
                </th>
              ))}
              {actions && (
                <th className="px-4 py-3.5 text-right w-20">
                  <span className="text-subheadline font-semibold text-light-text-secondary dark:text-dark-text-secondary tracking-tight">
                    <MoreHorizontal className="w-4 h-4 inline-block" />
                  </span>
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            <AnimatePresence mode="popLayout">
              {paginatedData.map((item, index) => (
                <motion.tr
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10, scale: 0.98 }}
                  transition={{
                    delay: index * 0.03,
                    duration: 0.25,
                    type: "spring",
                    damping: 25,
                    stiffness: 300
                  }}
                  whileHover={onRowClick ? {
                    backgroundColor: 'rgba(0, 0, 0, 0.03)',
                    scale: 1.005,
                    transition: { duration: 0.15 }
                  } : undefined}
                  whileTap={onRowClick ? { scale: 0.995 } : undefined}
                  className={cn(
                    'border-b border-black/[0.06] dark:border-white/[0.06] last:border-0',
                    onRowClick && 'cursor-pointer'
                  )}
                  onClick={() => onRowClick?.(item)}
                >
                  {columns.map((column) => (
                    <td
                      key={column.key}
                      className={cn(
                        'px-4 py-4',
                        column.align === 'center' && 'text-center',
                        column.align === 'right' && 'text-right'
                      )}
                    >
                      <div className="text-body text-light-text-primary dark:text-dark-text-primary tracking-tight">
                        {column.render ? column.render(item) : item[column.key]}
                      </div>
                    </td>
                  ))}
                  {actions && (
                    <td className="px-4 py-4 text-right" onClick={(e) => e.stopPropagation()}>
                      {actions(item)}
                    </td>
                  )}
                </motion.tr>
              ))}
            </AnimatePresence>
          </tbody>
        </table>
      </div>

      {/* Pagination - Liquid Glass */}
      {showPagination && totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-3 liquid-material-regular rounded-2xl shadow-glass-sm dark:shadow-dark-glass-sm">
          <div className="text-subheadline text-light-text-secondary dark:text-dark-text-secondary tracking-tight">
            Showing <span className="font-semibold text-light-text-primary dark:text-dark-text-primary">{startIndex + 1}</span> to{' '}
            <span className="font-semibold text-light-text-primary dark:text-dark-text-primary">{Math.min(endIndex, data.length)}</span> of{' '}
            <span className="font-semibold text-light-text-primary dark:text-dark-text-primary">{data.length}</span> results
          </div>

          <div className="flex items-center gap-2">
            {/* Previous button */}
            <motion.button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className={cn(
                'p-2 rounded-xl',
                'disabled:opacity-30 disabled:cursor-not-allowed',
                'hover:bg-black/[0.04] dark:hover:bg-white/[0.06]'
              )}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              transition={{ type: "spring", damping: 20, stiffness: 400 }}
              aria-label="Previous page"
            >
              <ChevronLeft className="w-5 h-5 text-light-text-secondary dark:text-dark-text-secondary" strokeWidth={2} />
            </motion.button>

            {/* Page numbers with animated indicator */}
            <div className="flex items-center gap-1 relative">
              {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                let pageNum: number
                if (totalPages <= 7) {
                  pageNum = i + 1
                } else if (currentPage <= 4) {
                  pageNum = i + 1
                } else if (currentPage >= totalPages - 3) {
                  pageNum = totalPages - 6 + i
                } else {
                  pageNum = currentPage - 3 + i
                }

                return (
                  <motion.button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={cn(
                      'min-w-[2rem] px-2 py-1 rounded-xl text-footnote font-semibold tracking-tight relative',
                      currentPage === pageNum
                        ? 'text-white'
                        : 'text-light-text-secondary dark:text-dark-text-secondary'
                    )}
                    whileHover={currentPage !== pageNum ? { scale: 1.05 } : undefined}
                    whileTap={{ scale: 0.95 }}
                    transition={{ type: "spring", damping: 20, stiffness: 400 }}
                  >
                    {/* Animated background for active page */}
                    {currentPage === pageNum && (
                      <motion.div
                        layoutId="activePageBg"
                        className="absolute inset-0 bg-gradient-to-b from-blue to-blue-hover rounded-xl shadow-glass-sm dark:shadow-dark-glass-sm"
                        transition={{ type: "spring", damping: 25, stiffness: 300 }}
                      />
                    )}
                    <span className="relative z-10">{pageNum}</span>
                  </motion.button>
                )
              })}
            </div>

            {/* Next button */}
            <motion.button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className={cn(
                'p-2 rounded-xl',
                'disabled:opacity-30 disabled:cursor-not-allowed',
                'hover:bg-black/[0.04] dark:hover:bg-white/[0.06]'
              )}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              transition={{ type: "spring", damping: 20, stiffness: 400 }}
              aria-label="Next page"
            >
              <ChevronRight className="w-5 h-5 text-light-text-secondary dark:text-dark-text-secondary" strokeWidth={2} />
            </motion.button>
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * SimpleTable - Lightweight table without pagination for small datasets
 */
interface SimpleTableProps<T> {
  data: T[]
  columns: Column<T>[]
  onRowClick?: (item: T) => void
}

export function SimpleTable<T extends Record<string, any>>({
  data,
  columns,
  onRowClick
}: SimpleTableProps<T>) {
  return (
    <div className="overflow-x-auto rounded-xl">
      <table className="w-full">
        <thead>
          <tr className="border-b border-black/[0.08] dark:border-white/[0.08]">
            {columns.map((column) => (
              <th
                key={column.key}
                className={cn(
                  'px-3 py-2 text-left',
                  'text-caption1 font-semibold text-light-text-tertiary dark:text-dark-text-tertiary tracking-tight uppercase'
                )}
              >
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((item, index) => (
            <tr
              key={index}
              className={cn(
                'border-b border-black/[0.04] dark:border-white/[0.04] last:border-0',
                onRowClick && 'cursor-pointer hover:bg-black/[0.02] dark:hover:bg-white/[0.02]'
              )}
              onClick={() => onRowClick?.(item)}
            >
              {columns.map((column) => (
                <td key={column.key} className="px-3 py-2.5">
                  <div className="text-subheadline text-light-text-primary dark:text-dark-text-primary tracking-tight">
                    {column.render ? column.render(item) : item[column.key]}
                  </div>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
